import { debounce, filter, map, noop, size } from 'lodash'
import React, { useCallback, useMemo } from 'react'
import { FormControl, InputGroup } from 'react-bootstrap'
import { OnChange } from 'react-final-form-listeners'

import Button from '../../../../Button'
import Field from '../../../../Field'
import { FullTextFormProps, IFilter } from '../../../types'

interface FilterRendererProps
  extends Pick<
    FullTextFormProps,
    | 'filters'
    | 'setFilter'
    | 'applyColor'
    | 'apply'
    | 'fulltextSearchLoading'
    | 'isMobile'
  > {
  additionalFtsFilters?: boolean
  handlePasteEvent?: (...args: any) => any
  handleKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
}

const DEBOUNCE_DELAY = 500

const FilterFulltext = ({
  filter,
  handleKeyDown,
  handlePasteEvent,
  applyColor,
  fulltextSearchLoading,
  apply,
  isMobile,
  setFilter,
}: {
  filter: IFilter
  handleKeyDown
  handlePasteEvent
  applyColor
  fulltextSearchLoading
  apply
  isMobile
  setFilter
}): React.ReactElement => {
  const onChange = useCallback(
    (value: any, prevValue: any) => {
      setFilter({
        name: filter.name,
        value: value === '' ? null : (value || '').trim(),
      })
      if (prevValue) {
        if (size(value) > 0 || (value === '' && value != null)) {
          apply(false, true)
        }
      }
    },
    [filter, apply],
  )

  const debouncedOnChange = useMemo(
    () => debounce(onChange, DEBOUNCE_DELAY),
    [onChange],
  )

  return (
    <React.Fragment>
      <OnChange name={filter.name ?? ''}>
        {(value: string, prevValue: string) => {
          debouncedOnChange(value, prevValue)
        }}
      </OnChange>
      <InputGroup className="d-flex">
        <Field name={filter.name as string} noMargin style={{ flex: '1' }}>
          <FormControl
            style={{ flex: '1' }}
            placeholder={`${filter.label}`}
            onKeyDown={
              handleKeyDown
                ? (event: React.KeyboardEvent<HTMLInputElement>) =>
                    handleKeyDown(event)
                : noop
            }
            onPaste={debounce(handlePasteEvent, 500)}
            autoComplete="new-password"
            autoFocus={
              (filter.autoFocus || filter.type === 'fulltext') && !isMobile
                ? true
                : undefined
            }
          />
        </Field>

        <InputGroup.Append>
          <Button
            variant={applyColor}
            onClick={fulltextSearchLoading ? noop : () => apply(false, false)}
            id="infinity-table__search-button"
          >
            <i
              className={
                fulltextSearchLoading
                  ? 'far fa-spinner-third fa-spin'
                  : 'far fa-search'
              }
            />
          </Button>
        </InputGroup.Append>
      </InputGroup>
    </React.Fragment>
  )
}

const FilterRenderer = (props: FilterRendererProps): React.ReactElement => {
  const {
    filters = [],
    setFilter,
    handleKeyDown,
    additionalFtsFilters,
    applyColor,
    apply = noop,
    fulltextSearchLoading,
    handlePasteEvent = noop,
    isMobile,
  } = props

  const renderFilters = (filter: IFilter): React.ReactElement | null => {
    if (filter.type === 'component') {
      return filter.component as any
    }
    return null
  }

  if (additionalFtsFilters) {
    return (
      <>
        {map(
          filter(
            filters,
            (filter) => filter.type !== 'fulltext' && filter.showForFts,
          ),
          (filter) => renderFilters(filter as any),
        )}
      </>
    )
  }

  return (
    <>
      {map(
        filter(
          filters,
          (filter) => filter.type === 'fulltext' && filter.showForFts,
        ),
        (filter) => (
          <FilterFulltext
            key={(filter as IFilter).name}
            filter={filter as any}
            handleKeyDown={handleKeyDown}
            handlePasteEvent={handlePasteEvent}
            applyColor={applyColor}
            fulltextSearchLoading={fulltextSearchLoading}
            apply={apply}
            isMobile={isMobile}
            setFilter={setFilter}
          />
        ),
      )}
    </>
  )
}

export default FilterRenderer
